import {
  debounce,
  safeFetch,
} from "../utilities/utils"

import { closeAllToggletips } from "../utilities/toggletip-engine"

class PredictiveSearch extends HTMLElement {
  constructor() {
    super()
    this.parser = new DOMParser()
    this.resultsContainers = this.querySelectorAll(".predictive-search")
    this.inputs = this.querySelectorAll(".search-input--desktop, .search-input--mobile")
    const recentSearches = sessionStorage.getItem("recentSearches")
    this.recentSearches = recentSearches ? JSON.parse(recentSearches) : []
    this.openCloseEngine = this.closest("toggletip-engine")
    this.toggletipEngines = document.querySelectorAll("toggletip-engine")

    this.addGlobalListeners()
    this.populateRecentSearches(this)
    this.addListeners()
  }

  clearRecentSearches() {
    this.recentSearches = []
    sessionStorage.removeItem("recentSearches")
    const recentSearches = this.querySelector(".recent-searches")

    if (recentSearches) {
      recentSearches.remove()
    }
  }

  removeRecentSearch(e) {
    const removeButton =
      e.target.type === "button" ? e.target : e.target.closest(".search-sidebar__remove")

    const term = removeButton.dataset.search
    if (!term) return

    const index = this.recentSearches.indexOf(term)
    if (index === -1) return

    this.recentSearches.splice(index, 1)
    sessionStorage.setItem("recentSearches", JSON.stringify(this.recentSearches))
    removeButton.closest(".recent-searches__item").remove()

    if (!this.recentSearches.length) {
      const recentSearches = this.querySelector(".recent-searches")

      if (recentSearches) {
        recentSearches.remove()
      }
    }
  }

  handleRecentSearchClick(e) {
    this.inputs.forEach((input) => {
      input.value = e.target.innerHTML
      this.search({ target: input })
    })
  }

  addRecentSearchesListeners() {
    const buttons = this.querySelectorAll(".search-sidebar__button")
    buttons.forEach((button) => {
      button.addEventListener("click", this.handleRecentSearchClick.bind(this))
    })

    const clearButton = this.querySelector(".search-sidebar__clear")

    clearButton?.addEventListener("click", this.clearRecentSearches.bind(this))

    const removeButtons = this.querySelectorAll(".search-sidebar__remove")
    removeButtons?.forEach((removeButton) => {
      removeButton.addEventListener("click", this.removeRecentSearch.bind(this))
    })
  }

  removeRecentSearchesListeners() {
    const buttons = this.querySelectorAll(".search-sidebar__button")
    buttons.forEach((button) => {
      button.removeEventListener("click", this.handleRecentSearchClick.bind(this))
    })

    const clearButton = this.querySelector(".search-sidebar__clear")

    clearButton?.removeEventListener("click", this.clearRecentSearches.bind(this))

    const removeButtons = this.querySelectorAll(".search-sidebar__remove")
    removeButtons?.forEach((removeButton) => {
      removeButton.removeEventListener("click", this.removeRecentSearch.bind(this))
    })
  }

  addListeners() {
    this.addRecentSearchesListeners()
  }

  close() {
    this.inputs.forEach((input) => {
      input.blur()
    })

    const resultsContainers = this.querySelectorAll(".predictive-search")

    if (resultsContainers) {
      resultsContainers.forEach((container) => {
        container.classList.remove("open")
      })
    }
  }

  populateRecentSearches(section) {
    const target = section || document
    const sidebar = target.querySelector("#search-sidebar")
    const recentSearches = target.querySelector(".recent-searches")

    if (recentSearches) {
      recentSearches.remove()
    }

    if (this.recentSearches.length) {
      const container = document.createElement("div")
      container.classList.add("search-sidebar__list", "recent-searches")

      const title = document.createElement("span")
      title.classList.add("search-sidebar__header", "text-md", "recent-searches__header")
      title.innerText = `${searchStrings.recent_searches}`

      const clearButton = document.createElement("button")
      clearButton.type = "button"
      clearButton.classList.add("search-sidebar__clear", "button--xs-secondary")
      clearButton.innerText = `${searchStrings.clear_all}`

      title.append(clearButton)
      container.append(title)

      const list = document.createElement("ul")

      this.recentSearches.forEach((search) => {
        const item = document.createElement("li")
        item.classList.add("search-sidebar__item", "recent-searches__item")
        const searchButton = document.createElement("button")
        searchButton.type = "button"
        searchButton.innerHTML = search
        searchButton.classList.add("search-sidebar__button")

        const removeButton = document.createElement("button")
        removeButton.type = "button"
        removeButton.classList.add("search-sidebar__remove")
        removeButton.innerHTML =
          "<span class='icon loaded' aria-hidden='true' focusable='false' role='presentation'>close</span>"
        removeButton.dataset.search = search
        item.appendChild(removeButton)
        item.appendChild(searchButton)
        list.appendChild(item)
      })

      container.appendChild(list)
      sidebar.prepend(container)
    }
  }

  globalSync(value) {
    const predictiveSearchesInputFields = document.querySelectorAll(
      "predictive-search .search-input--desktop, predictive-search .search-input--mobile",
    )

    predictiveSearchesInputFields.forEach((inputField) => {
      inputField.value = value
    })
  }

  addGlobalListeners() {
    this.inputs.forEach((input) => {
      input.addEventListener(
        "input",
        debounce((e) => {
          this.globalSync(e.target.value)
          this.search(e)
        }, 600),
      )
    })

    const searchInputMobileClose = this.querySelectorAll(".predictive-search__close")
    searchInputMobileClose.forEach((close) => {
      close.addEventListener("click", () => {
        this.openCloseEngine.close()
      })
    })

    const searchTriggers = Array.from(this.querySelectorAll(".search-trigger"))
    searchTriggers.forEach((trigger) => {
      if (trigger.tagName.toLowerCase() === "input" && trigger.type === "search") {
        trigger.addEventListener("focus", (e) => {
          if (!this.isOpen) {
            closeAllToggletips()
            this.openCloseEngine.open()

            setTimeout(() => {
              const searchInput = this.querySelector('.predictive-search__header input[type="search"]');
              if (searchInput) searchInput.focus();
            }, 50);
          }
        })

        trigger.addEventListener("click", (e) => {
          e.stopPropagation()
        })
      }
    })
  }

  setDefaultView() {
    if (this.defaultView) {
      const container = this.querySelector(".predictive-search__results-container")
      this.populateRecentSearches(this.defaultView)
      container.innerHTML = this.defaultView.innerHTML
      this.addRecentSearchesListeners()
    }
  }

  async search(event) {
    const id = event.target.getAttribute("data-predictive-search")
    const resultsContainer = this.querySelector(
      '#predictive-search[data-predictive-search="' + id + '"]',
    )
    const term = event.target.value
    const container = resultsContainer.querySelector(".predictive-search__results-container")
    const predictiveSearches = document.querySelectorAll("predictive-search")

    if (!term) {
      predictiveSearches.forEach((predictiveSearch) => predictiveSearch.setDefaultView())
      return
    }

    container.classList.add("loading")

    const url =
      window.Shopify.routes.root +
      `search/suggest?q=${term}&resources[options][fields]=title,product_type,variants.title,variants.sku,vendor&section_id=predictive-search`

    const { response, error } = await safeFetch(url)

    if (error || !response.ok) return

    const result = await response.text()

    const parsedSection = this.parser
      .parseFromString(result, "text/html")
      .querySelector("#shopify-section-predictive-search")

    const selectors = [".search-sidebar", ".search-products"]

    predictiveSearches.forEach((predictiveSearch) => {
      if (!predictiveSearch.recentSearches.includes(term)) {
        if (predictiveSearch.recentSearches.length > 4) predictiveSearch.recentSearches.pop()
        predictiveSearch.recentSearches.unshift(term)
        sessionStorage.setItem("recentSearches", JSON.stringify(predictiveSearch.recentSearches))
        predictiveSearch.setDefaultView()
      }

      this.populateRecentSearches(parsedSection)

      selectors.forEach((selector) => {
        const element = parsedSection.querySelector(selector)

        if (!element) return

        const container = predictiveSearch.querySelector(".predictive-search__results-container")

        const existingElement = container.querySelector(selector)

        if (!existingElement) return

        existingElement.innerHTML = element.innerHTML
      })

      predictiveSearch.addRecentSearchesListeners()
    })

    container.classList.remove("loading")
  }

  get isOpen() {
    return !!this.openCloseEngine.querySelector("[data-tooltip-trigger][open]")
  }

  open() {
    const predictiveSearchContainer = this.querySelector("#predictive-search")
    if (predictiveSearchContainer) {
      setTimeout(() => {
        const mobileInput = predictiveSearchContainer.querySelector(".search-input--mobile")
        const isMobile = window.matchMedia("(max-width: 1023px)").matches

        if (mobileInput && isMobile) mobileInput.focus()
      }, 100)

      if (!this.defaultView) {
        this.defaultView = predictiveSearchContainer
          .querySelector(".predictive-search__results-container")
          .cloneNode(true)
      }
    }
  }

  disconnectedCallback() {
    if (this.isOpen) this.close()
  }
}

if (!window.customElements.get("predictive-search")) {
  window.customElements.define("predictive-search", PredictiveSearch)
}
